import type { StatusTone } from '@hue/design-system';
import type { Entry, EntryStatus, PaidBy, PaymentMethod, PaymentTerm } from '../../lib/api.js';

/// O vocabulário do lançamento em português, em um lugar só: a listagem, o card do celular e a
/// ficha precisam dizer a mesma coisa pela mesma palavra.

export const methodLabels: Record<PaymentMethod, string> = {
  PIX: 'PIX',
  DEBIT: 'Débito',
  CASH: 'Dinheiro',
  BANK_SLIP: 'Boleto bancário',
  CREDIT: 'Crédito',
};

export const termLabels: Record<PaymentTerm, string> = {
  IMMEDIATE: 'À vista',
  DEFERRED: 'À prazo',
  INSTALLMENTS: 'Parcelado',
};

export const payerLabels: Record<PaidBy, string> = {
  PARTNER: 'Sócio',
  COMPANY: 'A própria empresa',
  THIRD_PARTY: 'Terceiros',
};

export const statusLabels: Record<EntryStatus, string> = {
  TO_REIMBURSE: 'A reembolsar',
  PARTIALLY_REIMBURSED: 'Parcialmente reembolsado',
  REIMBURSED: 'Reembolsado',
  PAID: 'Pago',
  PARTIALLY_PAID: 'Pago parcialmente',
  PAYMENT_PENDING: 'Pendente de pagamento',
};

/// A leitura da situação, não a cor dela: resolvido é verde, a meio caminho é azul, ainda deve é
/// âmbar. Quem pinta é a pílula do sistema.
export const statusTones: Record<EntryStatus, StatusTone> = {
  TO_REIMBURSE: 'warning',
  PARTIALLY_REIMBURSED: 'progress',
  REIMBURSED: 'positive',
  PAID: 'positive',
  PARTIALLY_PAID: 'progress',
  PAYMENT_PENDING: 'warning',
};

/// Situações que o operador escolhe. Nenhuma das trilhas declara quanto já foi pago: isso nasce da
/// baixa das parcelas, pela conciliação ou pela mão. A compra da empresa só entra na trilha de
/// pagamento; o "Pago" de sócio ou terceiro diz que aquele dinheiro ficou como capital (ADR 0025).
export function declarableStatuses(paidBy: PaidBy): EntryStatus[] {
  return paidBy === 'COMPANY' ? ['PAYMENT_PENDING'] : ['TO_REIMBURSE', 'PAID'];
}

/// As duas trilhas com baixa por parcela: o reembolso ao sócio e o pagamento da empresa. É o que
/// decide se a lista de parcelas existe para aquele lançamento (ADR 0025).
export function isSettleable(entry: Entry): boolean {
  return entry.declaredStatus === 'TO_REIMBURSE' || entry.declaredStatus === 'PAYMENT_PENDING';
}

/// O rótulo do dinheiro que já foi baixado, do jeito que aquela trilha o chama.
export function settledLabelOf(entry: Entry): string {
  return entry.declaredStatus === 'PAYMENT_PENDING' ? 'Pago' : 'Reembolsado';
}

/// Quem pagou, pelo nome quando há um. "Sócio" e "Terceiros" são categorias — o nome de quem
/// adiantou o dinheiro diz mais e é o que a pessoa procura na lista.
export function payerNameOf(entry: Entry): string {
  return entry.paidByPartner?.displayName ?? entry.thirdPartyName ?? payerLabels[entry.paidBy];
}
